import { supabase } from '../../lib/supabase';
import type { Band } from '../../types/database';
import { calculateDistance } from '../../utils/distance';

export type BandWithDistance = Band & {
  distance?: number;
};

export async function fetchFeaturedBands(
  limit: number = 5,
  coordinates?: { latitude: number; longitude: number } | null
) {
  try {
    const { data, error } = await supabase
      .from('bands')
      .select('*')
      .eq('is_claimed', true)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    const bandsWithDistance = (data || []).map(band => {
      if (coordinates && band.coordinates) {
        return {
          ...band,
          distance: calculateDistance(coordinates, band.coordinates),
        };
      }
      return band;
    });

    return bandsWithDistance;
  } catch (error) {
    console.error('Error fetching featured bands:', error);
    throw error;
  }
}

export async function fetchBands(
  page: number,
  itemsPerPage: number,
  searchQuery?: string,
  selectedGenres?: string[],
  selectedLocations?: string[],
  selectedStatuses?: string[],
  coordinates?: { latitude: number; longitude: number } | null,
  nearMeEnabled?: boolean
) {
  try {
    let query = supabase
      .from('bands')
      .select('*', { count: 'exact' })
      .eq('is_claimed', true);

    // Add search filter
    if (searchQuery && searchQuery.trim() !== '') {
      query = query.or(`name.ilike.%${searchQuery.trim()}%,description.ilike.%${searchQuery.trim()}%,location.ilike.%${searchQuery.trim()}%`);
    }

    // Add genre filter
    if (selectedGenres && selectedGenres.length > 0) {
      query = query.contains('genres', selectedGenres);
    }

    // Add location filter
    if (selectedLocations && selectedLocations.length > 0) {
      query = query.in('location', selectedLocations);
    }

    // Add status filter
    if (selectedStatuses && selectedStatuses.length > 0) {
      query = query.in('status', selectedStatuses);
    }

    // Always order by coordinates first (non-null first), then by creation date
    query = query
      .order('coordinates', { ascending: false, nullsLast: true })
      .order('created_at', { ascending: false });

    const start = page * itemsPerPage;
    query = query.range(start, start + itemsPerPage - 1);

    const { data, count, error } = await query;

    if (error) throw error;

    // Add distances if coordinates are provided
    const bandsWithDistance = (data || []).map(band => {
      if (nearMeEnabled && coordinates && band.coordinates) {
        return {
          ...band,
          distance: calculateDistance(coordinates, band.coordinates),
        };
      }
      return band;
    });

    // Sort by distance if near me is enabled
    if (nearMeEnabled && coordinates) {
      bandsWithDistance.sort((a, b) => {
        // Both have coordinates - sort by distance
        if (a.coordinates && b.coordinates) {
          return (a.distance || 0) - (b.distance || 0);
        }
        // One has coordinates - prioritize the one with coordinates
        if (a.coordinates) return -1;
        if (b.coordinates) return 1;
        // Neither has coordinates - maintain original order
        return 0;
      });
    }

    return { bands: bandsWithDistance, totalCount: count };
  } catch (error) {
    console.error('Error fetching bands:', error);
    throw error;
  }
}